import { Link, useLocation } from "@tanstack/react-router";
import { useT, LANG_LABEL, type Lang } from "@/lib/i18n";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import hvilogo from "@/assets/hvi-logo.webp";

export default function HVIHeader() {
  const location = useLocation();
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const isActive = (path: string) => location.pathname === path;

  const langs: Lang[] = ["en", "es", "fr", "de", "it", "pt", "zh", "ja", "ko", "pl", "ro", "th", "id", "fil", "hi", "tr", "ar", "ru"];

  const navLinks = [
    { to: "/", label: t("nav.dashboard") },
    { to: "/news", label: "News" },
    { to: "/theater", label: t("nav.theater") },
    { to: "/resources", label: t("nav.resources") },
    { to: "/about", label: t("nav.about") },
  ] as const;

  return (
    <header className="relative flex items-center justify-between px-3 md:px-6 py-3 border-b border-border bg-[var(--hvi-surface-container)]">
      <div className="flex items-center min-w-0">
        <Link to="/" aria-label="Hanta Virus International — Home" className="flex items-center">
          <img
            src={hvilogo}
            alt="Hanta Virus International"
            width={256}
            height={64}
            decoding="async"
            fetchPriority="high"
            className="h-12 md:h-16 object-contain select-none"
            style={{ aspectRatio: "256 / 64", width: "auto" }}
            draggable={false}
          />
        </Link>
      </div>
      <div className="flex items-center gap-1 md:gap-3">
        <span className="hidden sm:flex items-center gap-1.5 text-xs">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-red-400 font-bold uppercase text-[10px] md:text-xs">{t("live")}</span>
        </span>
        <nav className="hidden md:flex items-center gap-4 ml-4 text-xs uppercase tracking-wider">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`transition ${isActive(l.to) ? "text-hvi-primary border-b border-hvi-primary" : "text-hvi-on-surface-variant hover:text-hvi-primary"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Language Switcher */}
        <div className="relative">
          <button
            onClick={() => setOpen((v) => !v)}
            className="border border-border px-2 py-1 text-[10px] md:text-xs font-bold tracking-wider text-hvi-on-surface-variant hover:text-hvi-primary hover:border-hvi-primary transition flex items-center gap-1"
          >
            🌐 {LANG_LABEL[lang]}
            <span className="text-[8px]">▾</span>
          </button>
          {open && (
            <div className="absolute right-0 mt-1 z-50 bg-[var(--hvi-surface-container-high)] border border-border min-w-[80px] shadow-lg max-h-[60vh] overflow-y-auto">
              {langs.map((l) => (
                <button
                  key={l}
                  onClick={() => {
                    setLang(l);
                    setOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-xs transition hover:bg-[var(--hvi-surface-bright)] ${
                    l === lang ? "text-hvi-primary font-bold" : "text-hvi-on-surface-variant"
                  }`}
                >
                  {LANG_LABEL[l]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setNavOpen((v) => !v)}
          className="md:hidden border border-border p-1.5 text-hvi-on-surface-variant hover:text-hvi-primary hover:border-hvi-primary transition"
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
        >
          {navOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile nav drawer */}
      {navOpen && (
        <nav className="md:hidden absolute left-0 right-0 top-full z-40 bg-[var(--hvi-surface-container-high)] border-b border-border flex flex-col text-xs uppercase tracking-wider">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setNavOpen(false)}
              className={`px-4 py-3 border-t border-border transition ${
                isActive(l.to) ? "text-hvi-primary bg-[var(--hvi-surface-bright)]" : "text-hvi-on-surface-variant hover:text-hvi-primary"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
